import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useLocation } from "react-router-dom/cjs/react-router-dom.min"
import { useDropzone } from "react-dropzone"

import { SystemMessageContext } from "../../../context/SystemMessageContext"
import useCallApi from "../../../hooks/useCallApi"
import Image from "./Image/Image"
import Loading from "../../../templates/UI/Loading/Loading"
import callAPI from "../../../helpers/callAPI"
import ConfirmDeleteModal from "./ConfirmDeleteModal/ConfirmDeleteModal"
import Pagination from "../../../templates/UI/Pagination/Pagination"
import styles from './Image/Image.module.scss'
import useUrlState from "../../../hooks/useUrlState"

const MAX_FILES = 20
const PAGE_SIZE = 20

const ACCEPT = {
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "image/webp": [".webp"],
    "image/gif": [".gif"]
}

const ImageUpload = ({ }) => {

    document.title = "Image Upload"

    const location = useLocation()

    const { showSystemMessage } = useContext(SystemMessageContext)
    const [fileLocation, setFileLocation] = useState('mailers')
    const [isUploading, setIsUploading] = useState(false)
    const [queuedFiles, setQueuedFiles] = useState([])
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false)
    const [selectedImageKey, setSelectedImageKey] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [inputValue, setInputValue] = useUrlState('filter', '')

    const { dataLoaded, data, refetch } = useCallApi(`marketing/images/${fileLocation}`, 'GET', {}, [], showSystemMessage)

    let files = data?.files || []

    const filteredFiles = useMemo(() => {
        const filterText = inputValue.toLowerCase()

        if (!filterText) return files

        return files.filter(file =>
            file.Key.split("/").pop().toLowerCase().includes(filterText)
        )
    }, [files, inputValue])

    const totalPages = useMemo(() => (Math.max(1, Math.ceil(filteredFiles.length / PAGE_SIZE))), [filteredFiles.length])

    const pagedFiles = useMemo(() => {
        const startIndex = (currentPage - 1) * PAGE_SIZE

        return filteredFiles.slice(startIndex, startIndex + PAGE_SIZE)
    }, [filteredFiles, currentPage])


    useEffect(() => {
        // Reset page when folder changes
        setCurrentPage(1)
    }, [location])

    useEffect(() => {
        // If current page exceeds total pages (can happen after deletion), set it to last page
        if (currentPage > totalPages) {
            setCurrentPage(totalPages)
        }
    }, [currentPage, totalPages])

    const removeQueued = (name, size) => {
        setQueuedFiles(prev => prev.filter(file => !(file.name === name && file.size === size)))
    }

    const uploadFiles = (e) => {
        e.preventDefault()
        const formData = new FormData()
        for (let i = 0; i < queuedFiles.length; i++) {
            formData.append(`files[${i}]`, queuedFiles[i])
        }
        setIsUploading(true)
        callAPI(`marketing/images/${fileLocation}`, 'POST', formData, true)
            .then((result) => {
                if (result.success) {
                    showSystemMessage('success', 'Images uploaded successfully')
                    setQueuedFiles([])
                    refetch()
                } else {
                    showSystemMessage('error', result.errors.message)
                }
                setIsUploading(false)
            })
            .catch(() => {
                showSystemMessage('error', 'An error occurred while uploading images.')
                setIsUploading(false)
            })
    }

    const clearQueue = () => {
        setQueuedFiles([])
    }

    const addFilesToQueue = useCallback((files) => {
        if (!files.length) return

        setQueuedFiles(prev => {
            const merged = [...prev, ...files]

            if (merged.length > MAX_FILES) {
                showSystemMessage(`Max ${MAX_FILES} files allowed.`)
            }
            return merged.slice(0, MAX_FILES)
        })
    }, [showSystemMessage])

    const onDropAccepted = useCallback((acceptedFiles) => {
        addFilesToQueue(acceptedFiles)
    }, [addFilesToQueue])

    const onDropRejected = useCallback((rejectedFiles) => {
        if (!rejectedFiles.length) return

        const first = rejectedFiles[0]
        const name = first?.file?.name || 'File'
        showSystemMessage(`Rejected: ${name}. Only image files are allowed.`)
    }, [showSystemMessage])


    const handleDeleteClick = (key) => {
        setSelectedImageKey(key)
        setShowConfirmDeleteModal(true)
    }

    const handleDeleteConfirm = () => {
        callAPI(`marketing/${selectedImageKey}`, 'DELETE')
            .then((result) => {
                if (result.success) {
                    showSystemMessage('success', 'Image deleted successfully')
                    refetch()
                } else {
                    showSystemMessage('error', result.errors.message)
                }
            })
            .catch(() => {
                showSystemMessage('error', 'An error occurred while deleting the image.')
            })
    }

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDropAccepted,
        onDropRejected,
        accept: ACCEPT,
        multiple: true,
        noClick: true,
        noKeyboard: true,
        disabled: isUploading
    })

    return (
        <div>
            <ConfirmDeleteModal isOpen={showConfirmDeleteModal} setIsOpen={setShowConfirmDeleteModal} onConfirm={handleDeleteConfirm} />
            <div className="page-header d-flex align-items-center">
                <h1 className="mb-h1">Image Upload</h1>
            </div>
            <div className="card">
                <div className="card-header">
                    <div className="d-flex align-items-center text-weight-normal">
                        <select className="form-control form-control-sm w-auto ml-2" value={fileLocation} onChange={(e) => setFileLocation(e.target.value)}>
                            <option value="">Choose Location</option>
                            <option value="mailers">Mailers</option>
                        </select>
                    </div>
                </div>
                <div className="card-body">
                    {/* Area to drag and drop images */}
                    <div
                        className={`border rounded p-4 text-center ${isDragActive ? 'border-primary bg-light' : 'border-secondary'}`}
                        {...getRootProps()}>
                        <input {...getInputProps()} />
                        <p className="mb-2">Drag and Drop Images Here</p>
                        <p className="mb-0 text-muted">or</p>
                        <button type="button" className="btn btn-sm btn-outline-primary mt-2" onClick={open} disabled={isUploading}>
                            Choose Files
                        </button>
                    </div>

                    {/* Area that shows selected images (not uploaded yet) */}
                    {queuedFiles.length > 0 && (
                        <div className="mt-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <strong>Selected Images ({queuedFiles.length})</strong>
                                <button type="button" className="btn btn-sm btn-link p-0" onClick={clearQueue} disabled={isUploading}>
                                    Clear All
                                </button>
                            </div>

                            <div className="list-group mb-3">
                                {queuedFiles.map(file => (
                                    <div key={`${file.name}-${file.size}`} className="list-group-item d-flex justify-content-between">
                                        <span>{file.name}</span>
                                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeQueued(file.name, file.size)} disabled={isUploading}>
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={uploadFiles}
                                disabled={isUploading}
                            >
                                {isUploading ? "Uploading..." : "Upload"}
                            </button>
                        </div>
                    )}
                    <hr />

                    <div className="d-flex align-items-center m-2">
                        <div className="mr-2 font-weight-bold">Filter:</div>
                        <input className="form-control mr-2" value={inputValue} onChange={(e) => {
                            setInputValue(e.target.value)
                            setCurrentPage(1)
                        }} placeholder="Search images by name" />
                    </div>

                    <hr />
                    <Pagination totalPages={totalPages} currentPage={currentPage} handlePageChange={setCurrentPage} />

                    {dataLoaded ? (
                        <div className={styles.gallery}>
                            {pagedFiles.map(image =>
                                <Image key={image.Key} name={image.Key.split('/').pop()} image={image} handleDelete={() => handleDeleteClick(image.Key)} />
                            )}
                        </div>
                    ) : (
                        <Loading />
                    )}
                </div>
            </div>
        </div>
    )
}

export default ImageUpload